import { useState, useCallback, createContext, useContext, type ReactNode } from "react";
import { useWindowDimensions } from "react-native";
import { View } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { useTenant } from "@src/features/tenants";
import { useNotificationPanel } from "@src/hooks/useNotificationPanel";
import { NotificationSidePanel } from "@visioni/a3s-activity/app";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { useSidebarStore, type SidebarViewport } from "@src/stores/sidebar-store";
import { Sidepanel } from "./Sidepanel.web";
import { WebHeader } from "./WebHeader.web";
import { TenantSwitcherPanel } from "./TenantSwitcherPanel.web";
import { RightDrawer } from "./RightDrawer.web";

interface TenantSwitcherControl {
  open: () => void;
}

const TenantSwitcherContext = createContext<TenantSwitcherControl | null>(null);

export function useTenantSwitcherControl(): TenantSwitcherControl | null {
  return useContext(TenantSwitcherContext);
}

interface WebLayoutProps {
  children?: ReactNode;
  rightPanel?: ReactNode;
}

export function WebLayout({ children, rightPanel }: WebLayoutProps) {
  // Narrow viewports get the icon rail by default and wide ones the full
  // sidebar, but either can be toggled and each viewport remembers its own
  // choice — so resizing never fights the user, and no effect has to
  // reconcile viewport against stored state.
  const viewport: SidebarViewport = useWebLayout() ? "wide" : "narrow";
  const collapsed = useSidebarStore((s) => s.collapsed[viewport]);
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed);
  const handleToggleCollapse = useCallback(() => {
    toggleCollapsed(viewport);
  }, [toggleCollapsed, viewport]);
  const [tenantSwitcherVisible, setTenantSwitcherVisible] = useState(false);
  const { tenants } = useTenant();
  const hasMultipleTenants = tenants.length > 1;
  const isFeedPanelEnabled = useBooleanFlag(FEATURE_FLAGS.ACTIVITY_FEED_PANEL);
  const notificationPanel = useNotificationPanel({ enabled: isFeedPanelEnabled });

  const handleOpenTenantSwitcher = useCallback(() => {
    setTenantSwitcherVisible(true);
  }, []);

  const handleCloseTenantSwitcher = useCallback(() => {
    setTenantSwitcherVisible(false);
  }, []);

  const switcherControl = hasMultipleTenants ? { open: handleOpenTenantSwitcher } : null;

  return (
    <TenantSwitcherContext.Provider value={switcherControl}>
      <View flex={1} flexDirection="row" bg="background" width="100%" height="100%" p="3xl" gap="3xl">
        <Sidepanel
          collapsed={collapsed}
          onToggleCollapse={handleToggleCollapse}
          onOpenTenantSwitcher={hasMultipleTenants ? handleOpenTenantSwitcher : undefined}
        />

        <View flex={1} height="100%" style={{ overflow: "visible" }}>
          <WebHeader
            onNotificationsPress={isFeedPanelEnabled ? notificationPanel.onOpen : undefined}
            hasUnread={isFeedPanelEnabled && notificationPanel.hasUnread}
          />

          <View flex={1} flexDirection="row" gap="3xl">
            <View flex={1} bg="background">
              {children}
            </View>

            {rightPanel && (
              <View
                width={sizes.webRightPanelWidth}
                borderLeftWidth={0.5}
                borderColor="borderCard"
                pl="3xl"
                pr="3xl"
                pt="8xl"
                pb="3xl"
              >
                {rightPanel}
              </View>
            )}
          </View>
        </View>
      </View>

      {hasMultipleTenants && (
        <TenantSwitcherPanel visible={tenantSwitcherVisible} onClose={handleCloseTenantSwitcher} />
      )}
      {isFeedPanelEnabled && (
        <RightDrawer
          visible={notificationPanel.visible}
          onClose={notificationPanel.onClose}
          borderRadius={0}
          scrollable={false}
        >
          <NotificationSidePanel
            activities={notificationPanel.activities}
            hasMore={notificationPanel.hasMore}
            isLoadingMore={notificationPanel.isLoadingMore}
            onMarkAllRead={notificationPanel.onMarkAllRead}
            onNavigate={notificationPanel.onNavigate}
            onClose={notificationPanel.onClose}
            onLoadMore={notificationPanel.onLoadMore}
          />
        </RightDrawer>
      )}
    </TenantSwitcherContext.Provider>
  );
}

export function useWebLayout(): boolean {
  const { width: windowWidth } = useWindowDimensions();
  return windowWidth > sizes.webLayoutBreakpoint;
}
